import React from 'react'
import { Button } from '@wove/web-ui'
import { Edit, Trash2, Database } from 'lucide-react'
import { CustomFieldMapping } from '@wove/api'

interface CustomFieldMappingsTableProps {
  mappings: CustomFieldMapping[]
  onEdit: (mapping: CustomFieldMapping) => void
  onDelete: (mapping: CustomFieldMapping) => void
  isLoading: boolean
}

const DataTypeIcon = ({ dataType }: { dataType: string }) => {
  const icons = {
    text: '📝',
    number: '🔢',
    boolean: '✓',
    date: '📅',
  }
  return <span className="text-lg">{icons[dataType as keyof typeof icons] || '❓'}</span>
}

const DataTypeBadge = ({ dataType }: { dataType: string }) => {
  const colors = {
    text: 'bg-blue-100 text-blue-800',
    number: 'bg-green-100 text-green-800',
    boolean: 'bg-purple-100 text-purple-800',
    date: 'bg-orange-100 text-orange-800',
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[dataType as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
      {dataType}
    </span>
  )
}

export default function CustomFieldMappingsTable({
  mappings,
  onEdit,
  onDelete,
  isLoading,
}: CustomFieldMappingsTableProps) {
  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
          <span className="ml-2 text-gray-600">Loading custom field mappings...</span>
        </div>
      </div>
    )
  }

  if (mappings.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8">
        <div className="text-center">
          <Database className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No custom field mappings</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new custom field mapping.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Field Key
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Value
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Updated
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {mappings.map((mapping) => (
              <tr key={mapping.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <DataTypeIcon dataType={mapping.dataType} />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {mapping.key}
                      </div>
                      <div className="text-sm text-gray-500">
                        ID: {mapping.id}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <DataTypeBadge dataType={mapping.dataType} />
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {mapping.value ? (
                      <span className="max-w-xs truncate block" title={mapping.value}>
                        {mapping.dataType === 'boolean' 
                          ? (mapping.value === 'true' || mapping.value === '1' ? 'True' : 'False')
                          : mapping.value
                        }
                      </span>
                    ) : (
                      <span className="text-gray-400 italic">No value</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(mapping.updatedAt).toLocaleDateString()}
                  <div className="text-xs text-gray-400">
                    {new Date(mapping.updatedAt).toLocaleTimeString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(mapping)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(mapping)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}